import { useEffect, useState } from 'react'
import { ToastContainer } from 'react-toastify'

import { App } from './App'
import codeplatformJson from './codeplatform.json'
import { FirebaseConfig, FirebaseProvider } from './context'
import type { SandboxChatUser } from './utils/sandboxChatAuth'
// import { useVersionController } from "./hooks";
import {
  Config,
  getLanguageFromStorage,
  getThemeFromStorage,
  languageKey,
  postToParent,
  READY,
  setParentOrigin,
  themeKey,
} from './utils'

const inIframe = window.location !== window.parent.location
const getTrustedParentOrigin = () => {
  if (!inIframe || !document.referrer) {
    return undefined
  }

  try {
    const origin = new URL(document.referrer).origin
    const { hostname } = new URL(origin)
    return hostname === 'localhost' ||
      hostname === 'backoffice.invisiblecfo.com' ||
      hostname === 'codeplatform.com' ||
      hostname.endsWith('.codeplatform.com')
      ? origin
      : undefined
  } catch {
    return undefined
  }
}

const parentOrigin = getTrustedParentOrigin()
const waitForMessage = Boolean(parentOrigin)
setParentOrigin(parentOrigin)

export const AppWrapper = () => {
  const [wait, setWait] = useState(waitForMessage)
  const [inDevice, setInDevice] = useState(false)
  const [webFirebaseConfig, setWebFirebaseConfig] = useState<FirebaseConfig>()
  const [version, setVersion] = useState<string>()
  const [projectId, setProjectId] = useState<string>()
  const [previewHost, setPreviewHost] = useState<string>()
  const [sandboxUser, setSandboxUser] = useState<SandboxChatUser>()
  const [firestorePrefix, setFirestorePrefix] = useState<string>()
  const [config, setConfig] = useState(codeplatformJson as Config)
  const [language, setLanguage] = useState(getLanguageFromStorage())
  const [theme, setTheme] = useState(getThemeFromStorage())
  const [email, setEmail] = useState<string>()
  const [token, setToken] = useState<string>()
  // useVersionController();

  const [desktopMode, setDesktopMode] = useState(window.innerWidth >= 680)
  const [wideDesktopMode, setWideDesktopMode] = useState(window.innerWidth >= 1032)

  useEffect(() => {
    localStorage.setItem(languageKey, language)
  }, [language])

  useEffect(() => {
    localStorage.setItem(themeKey, theme)
  }, [theme])

  useEffect(() => {
    if (wait) {
      const onMessage = ({ data, origin, source }: MessageEvent) => {
        if (source !== window.parent || origin !== parentOrigin) {
          return
        }
        if (data && typeof data === 'object') {
          const {
            inDevice,
            projectId,
            previewHost,
            sandboxUser,
            legacy,
            webFirebaseConfig,
            version,
            firestorePrefix,
            config,
            language,
            theme,
            email,
            token,
          } = data
          if (
            inDevice !== undefined &&
            projectId &&
            version &&
            config &&
            language &&
            theme &&
            (previewHost || webFirebaseConfig)
          ) {
            setInDevice(inDevice)
            setProjectId(projectId)
            setPreviewHost(previewHost)
            setSandboxUser(previewHost ? sandboxUser : undefined)
            setWebFirebaseConfig(previewHost ? undefined : webFirebaseConfig)
            setVersion(version)
            setFirestorePrefix(legacy === true ? firestorePrefix : undefined)
            setConfig(config)
            setLanguage(language)
            setTheme(theme)
            setEmail(email)
            setToken(token)
            setWait(false)
          } else if (inDevice !== undefined) {
            setInDevice(inDevice)
            setWait(false)
          }
        }
      }
      window.addEventListener('message', onMessage)
      postToParent(READY)
      return () => {
        window.removeEventListener('message', onMessage)
      }
    }
  }, [])

  useEffect(() => {
    const resize = () => {
      setDesktopMode(window.innerWidth >= 680)
      setWideDesktopMode(window.innerWidth >= 1032)
    }
    window.addEventListener('resize', resize)
    return () => {
      window.removeEventListener('resize', resize)
    }
  }, [])

  return wait ? (
    <></>
  ) : (
    <>
      <ToastContainer theme={theme} />
      <FirebaseProvider
        webFirebaseConfig={webFirebaseConfig}
        projectId={projectId}
        previewHost={previewHost}
        sandboxUser={sandboxUser}
        version={version}
        firestorePrefix={firestorePrefix}
        inDevice={inDevice}
        language={language}
        theme={theme}
        setTheme={setTheme}
        desktopMode={desktopMode}
        config={config}
      >
        <App
          inDevice={inDevice}
          language={language}
          setLanguage={setLanguage}
          theme={theme}
          setTheme={setTheme}
          desktopMode={desktopMode}
          wideDesktopMode={wideDesktopMode}
          config={config}
          email={email}
          token={token}
        />
      </FirebaseProvider>
    </>
  )
}
